import {
  AuthorizationStatusEnum,
  PreTransaction,
} from "@/app/services/authorizationService/getAuthorization";
import { useAuthorizationId } from "@/app/hooks/useAuthorizationId";
import { Sheet, SheetContent } from "@/view/components/ui/sheet";
import { Spinner } from "@/view/components/ui/spinner";
import { authorizationStatusMap } from "@/app/utils/authorization";
import Status from "@/view/components/Status";
import { format } from "date-fns";
import { DetailItem } from "./DetailItem";
import { Button } from "@/view/components/ui/button";
import { useToast } from "@/view/components/ui/use-toast";
import { Copy } from "lucide-react";
import { PartnerType } from "@/app/utils/partners.enum";
import { useProductsCatalog } from "@/app/hooks/useProductsCatalog";
import { SupportRequestButton } from "@/view/components/SupportRequestButton";

type Props = {
  open: boolean;
  setOpen: (v: boolean) => void;
  selectedItem: PreTransaction | null;
  onSimulate: () => void;
};

export const AuthorizationSheet = ({
  open,
  setOpen,
  selectedItem,
  onSimulate,
}: Props) => {
  const { toast } = useToast();
  const { getProductLabel } = useProductsCatalog();
  const { data, isLoading, isError, error } = useAuthorizationId(
    selectedItem?._id,
    selectedItem?.type,
  );

  const limitChars = (
    value: string | number | undefined | null,
    max: number,
  ) => {
    if (!value) return "";

    const str = String(value);

    if (str.length <= max) return str;

    return str.slice(0, max) + "...";
  };

  const handleCopyLink = async () => {
    if (!data?.authorizationLink) return;

    await navigator.clipboard.writeText(data.authorizationLink);

    toast({
      title: "Link copiado com sucesso",
      variant: "default",
    });
  };

  const isAuthorized = data?.status === AuthorizationStatusEnum.AUTHORIZED;

  const isLinkExpired = data?.linkExpirationDate
    ? new Date(data.linkExpirationDate) < new Date()
    : false;

  const isCrefazClt =
    (selectedItem?.type ?? data?.type) === PartnerType.CREFAZ_CLT;

  const isCrefazCltPending =
    isCrefazClt && data?.status === AuthorizationStatusEnum.PENDING;

  const canShowSimulationButton =
    (isAuthorized && !isLinkExpired) || isCrefazCltPending;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-[340px] overflow-y-auto rounded-2xl bg-[#F4F5F7] p-2 xs:mr-5 xs:mt-5 xs:h-[95%] xs:w-96 xs:p-5">
        {isLoading && (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        )}

        {isError && (
          <div className="flex h-full flex-col items-center justify-center px-4 text-center">
            <p className="font-semibold">Erro ao carregar autorização</p>
            <p className="mt-1 text-sm text-gray-500">
              {(error as any)?.message || "Tente novamente mais tarde"}
            </p>
          </div>
        )}

        {data && (
          <div className="flex h-full flex-col">
            <div>
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-bold">Detalhes da autorização</h2>

                <SupportRequestButton
                  context={{
                    product: data?.type ?? undefined,
                    metadata: {
                      authorizationId: data._id,
                      customer: data?.customer?.name,
                      cpf: data?.customer?.cpf,
                      status: data?.status,
                    },
                  }}
                  title="Abrir chamado sobre esta autorização"
                />
              </div>
              <div className="my-5 w-full border-b-[1px] border-gray01" />

              <div className="space-y-2 pb-5">
                {data?.customer?.name && (
                  <DetailItem label="Cliente" content={data.customer.name} />
                )}

                <DetailItem
                  label="Produto"
                  content={getProductLabel(data?.type ?? PartnerType.C6)}
                />

                {!isCrefazClt && data?.authorizationLink && (
                  <DetailItem
                    label="Link de autorização"
                    content={
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-black01">
                          {limitChars(data.authorizationLink, 16)}
                        </p>

                        <Copy
                          size={16}
                          className="cursor-pointer transition-opacity hover:opacity-70"
                          onClick={handleCopyLink}
                        />
                      </div>
                    }
                  />
                )}

                {data?.status && (
                  <DetailItem
                    label="Status"
                    content={
                      <Status status={authorizationStatusMap[data?.status]} />
                    }
                  />
                )}

                {!isCrefazClt && data?.linkExpirationDate && (
                  <DetailItem
                    label="Data de expiração"
                    content={format(
                      new Date(data?.linkExpirationDate),
                      "dd/MM/yyyy, HH:mm",
                    )}
                  />
                )}

                {data?.createdAt && (
                  <DetailItem
                    label="Data de criação"
                    content={format(
                      new Date(data?.createdAt),
                      "dd/MM/yyyy, HH:mm",
                    )}
                  />
                )}

                {data?.observation && (
                  <DetailItem
                    label="Observação"
                    type="row"
                    content={data.observation}
                    contentClassName="whitespace-normal break-words"
                  />
                )}
              </div>
            </div>

            {canShowSimulationButton && (
              <Button
                className="mt-auto w-full text-white"
                onClick={onSimulate}
              >
                {isCrefazCltPending
                  ? "Acompanhar autorização"
                  : "Realizar simulação"}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
